import React, { useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Twitter, Youtube, ArrowRight, Check } from "lucide-react";
import { subscribeNewsletter } from "../lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await subscribeNewsletter(email);
      setDone(true);
      toast.success(
        res.status === "already_subscribed"
          ? "You're already on the line."
          : "Welcome to the line."
      );
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="footer" className="relative bg-black pt-24 overflow-hidden">
      {/* Newsletter */}
      <div className="px-6 md:px-12 max-w-3xl mb-24">
        <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-steel-light mb-5">
          Board the line
        </p>
        <h3 className="font-display text-3xl md:text-5xl font-bold tracking-tight text-white mb-8 leading-tight">
          Get first access to drops, before they vanish.
        </h3>
        <form onSubmit={submit} className="flex items-center border-b border-white/25 pb-3 max-w-lg">
          <input
            data-testid="newsletter-input"
            type="email"
            required
            value={email}
            disabled={done}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="flex-1 bg-transparent text-white placeholder:text-steel focus:outline-none font-mono-tech text-sm"
          />
          <button
            data-testid="newsletter-submit"
            type="submit"
            disabled={loading || done}
            className="text-white hover:text-steel-light transition-colors duration-300 disabled:opacity-50"
            aria-label="Subscribe"
          >
            {done ? <Check className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
          </button>
        </form>
      </div>

      {/* Oversized wordmark */}
      <div className="px-6 md:px-12 border-t border-white/10 pt-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <div className="flex gap-6">
            {[
              { Icon: Instagram, href: "https://instagram.com/ghostline.wor1d", label: "Instagram" },
              { Icon: Twitter, href: "#", label: "Twitter" },
              { Icon: Youtube, href: "#", label: "YouTube" },
            ].map(({ Icon, href, label }, i) => (
              <a
                key={i}
                href={href}
                target={href === "#" ? undefined : "_blank"}
                rel={href === "#" ? undefined : "noopener noreferrer"}
                data-testid={`social-link-${i}`}
                className="text-steel-light hover:text-white transition-colors duration-300"
                aria-label={label}
              >
                <Icon className="w-5 h-5" />
              </a>
            ))}
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2 font-mono-tech text-[11px] uppercase tracking-widest text-steel-light">
            <span>Stockists</span>
            <span>Shipping</span>
            <span>Careers</span>
            <span>Legal</span>
          </div>
        </div>

        <h2 className="font-display font-extrabold tracking-tighter text-white leading-none mt-16 text-[19vw] md:text-[15vw]">
          GHOSTLINE
        </h2>
      </div>

      {/* Animated train line */}
      <div className="relative h-16 border-t border-white/10 overflow-hidden">
        <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-white/15" />
        <motion.div
          data-testid="train-line-graphic"
          className="absolute top-1/2 -translate-y-1/2 flex items-center gap-1"
          initial={{ left: "-10%" }}
          animate={{ left: "110%" }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        >
          <span className="block w-16 h-[3px] bg-white" />
          <span className="block w-8 h-[3px] bg-white/60" />
          <span className="block w-3 h-[3px] bg-white/30" />
        </motion.div>
      </div>

      <div className="px-6 md:px-12 py-6 flex items-center justify-between font-mono-tech text-[10px] uppercase tracking-widest text-steel">
        <span>© {new Date().getFullYear()} Ghostline</span>
        <span>Leave an echo worth remembering</span>
      </div>
    </footer>
  );
}
